import { useEffect, useRef, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData } from 'react-router-dom'
import { useCurrentHealthCenter, useCurrentMode, useIsAccountClosed } from '../../components/AppShell'
import { PatientRow, PatientsIndex, fetchPatients, findOrCreatePatient, toggleActivePatient } from '../../api/patients'
import { getMe } from '../../api/me'
import { ApiError } from '../../lib/api'
import { resolveMode } from '../../lib/mode'
import { trackEvent } from '../../lib/config'
import {
  Alert,
  ButtonAnchor,
  CheckboxField,
  DataTable,
  Dropdown,
  PageHeading,
  Panel,
  PanelBody,
  TableColumn,
  useToast,
} from '../../components/common'
import { useImpression } from '../../lib/useImpression'
import { useNavigateTo } from '../../lib/spaPath'
import { getAccountPrompt, trackUpgradePromptClick } from '../../lib/upgradePrompt'
import { NewPatientModal } from './NewPatientModal'
import styles from './PatientsIndexPage.module.scss'

const PAGE_LENGTH = 10
const SEARCH_DELAY_MS = 750

const FIRST_PAGE_ORDER = { orderColumn: 0, orderDir: 'desc' } as const

export async function loader({ params, request }: LoaderFunctionArgs): Promise<PatientsIndex> {
  const healthCenterId = Number(params.healthCenterId)
  const me = await getMe()
  const healthCenter = me.health_centers.find((center) => center.id === healthCenterId)
  const mode = healthCenter ? resolveMode(healthCenter, new URL(request.url).searchParams.get('mode')) : null
  return fetchPatients(healthCenterId, { start: 0, length: PAGE_LENGTH, search: '', archivedOnly: false, mode, ...FIRST_PAGE_ORDER })
}

export default function PatientsIndexPage() {
  const healthCenter = useCurrentHealthCenter()
  const mode = useCurrentMode()
  const closed = useIsAccountClosed()
  const showToast = useToast()
  const navigateTo = useNavigateTo()
  const loaded = useLoaderData() as PatientsIndex
  const [data, setData] = useState<PatientsIndex>(() => loaded)
  const [page, setPage] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [orderColumn, setOrderColumn] = useState<number>(FIRST_PAGE_ORDER.orderColumn)
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>(FIRST_PAGE_ORDER.orderDir)
  const [modalOpen, setModalOpen] = useState(false)
  const [welcomeDismissed, setWelcomeDismissed] = useState(false)
  const isContability = mode === 'contability'

  const reload = async () => {
    if (!healthCenter) {
      return
    }
    setData(
      await fetchPatients(healthCenter.id, {
        start: page * PAGE_LENGTH,
        length: PAGE_LENGTH,
        search,
        archivedOnly: showArchived,
        orderColumn,
        orderDir,
        mode,
      }),
    )
  }

  const query = JSON.stringify([page, search, showArchived, orderColumn, orderDir])
  const loadedQuery = useRef(query)
  useEffect(() => {
    if (loadedQuery.current !== query) {
      loadedQuery.current = query
      void reload()
    }
  }, [query])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      // searchInput and search only differ once the professional has typed, which
      // keeps the mount from booking a search nobody made.
      if (searchInput !== search) {
        trackEvent('Patients Searched', { has_query: searchInput.length > 0, query_length: searchInput.length, show_archived: showArchived })
      }
      setSearch(searchInput)
      setPage(0)
    }, SEARCH_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const upgradePrompt = healthCenter ? getAccountPrompt(healthCenter) : null
  useImpression('Upgrade Prompt Shown', upgradePrompt ? { prompt: upgradePrompt, surface: 'patients' } : null)

  const welcomeHint = data.patientsTotal > 1 ? null : data.patientsTotal === 0 ? 'welcome' : 'first_patient_created'
  useImpression('Onboarding Hint Shown', welcomeHint && !welcomeDismissed ? { hint: welcomeHint } : null)

  if (!healthCenter) {
    return null
  }

  const dismissWelcome = () => {
    trackEvent('Onboarding Hint Dismissed', { hint: welcomeHint })
    setWelcomeDismissed(true)
  }

  const handleUpgradePromptClick = upgradePrompt ? trackUpgradePromptClick(upgradePrompt, 'patients') : undefined

  const total = data.recordsFiltered
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LENGTH))
  const firstShown = total === 0 ? 0 : page * PAGE_LENGTH + 1
  const lastShown = Math.min(total, (page + 1) * PAGE_LENGTH)

  const linkCell = (row: PatientRow, value: string | number) => (
    <a
      className="default-text-color"
      href={row.record_path}
      onClick={() => trackEvent('Clinical Record Opened', { source: 'patients_table' }, { transport: 'sendBeacon' })}
    >
      {value}
    </a>
  )

  const blankToDash = (value: string | number | null) => (value === null || value === '' ? '-' : value)

  const columns: readonly TableColumn<PatientRow>[] = [
    { key: '0', label: 'Nº', sortable: true, render: (row) => linkCell(row, blankToDash(row.number_of_clinical_record)) },
    { key: '1', label: 'Nombre', sortable: true, render: (row) => linkCell(row, blankToDash(row.name)) },
    { key: '2', label: 'RUT', sortable: true, render: (row) => linkCell(row, blankToDash(row.rut)) },
    { key: '3', label: 'Número de teléfono', sortable: true, render: (row) => linkCell(row, blankToDash(row.phone_number)) },
    { key: '4', label: 'Ente que deriva', sortable: true, render: (row) => linkCell(row, blankToDash(row.company_which_derives)) },
    ...(isContability
      ? []
      : [{ key: 'sessions', label: 'Nº Sesiones', render: (row: PatientRow) => linkCell(row, blankToDash(row.interviews_count)) }]),
    ...(closed
      ? []
      : [
          {
            key: 'archive',
            label: 'Archivar',
            render: (row: PatientRow) => (
              <a
                href="#"
                className="default-text-color"
                rel="nofollow"
                onClick={(event) => {
                  event.preventDefault()
                  void handleToggleActive(row)
                }}
              >
                <i
                  className={row.active ? 'fa fa-archive' : 'fa fa-history'}
                  style={{ color: row.active ? '#FF1744' : '#00C853', marginLeft: 4 }}
                />{' '}
                {row.active ? 'Archivar' : 'Restaurar'}
              </a>
            ),
          },
        ]),
  ]

  const handleSort = (key: string) => {
    const column = Number(key)
    if (Number.isNaN(column)) {
      return
    }
    if (orderColumn === column) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderColumn(column)
      setOrderDir('asc')
    }
    setPage(0)
  }

  const handleToggleActive = async (row: PatientRow) => {
    const result = await toggleActivePatient(row.toggle_active_path)
    trackEvent(showArchived ? 'Patient Restored' : 'Patient Archived')
    showToast('success', result.notice)
    await reload()
  }

  const handleFindOrCreate = async (rut: string) => {
    try {
      const result = await findOrCreatePatient(healthCenter.id, rut, mode)
      trackEvent('Patient Created', { source: 'rut_lookup', has_rut: rut.length > 0 }, { transport: 'sendBeacon' })
      navigateTo(result.redirect)
    } catch (requestError) {
      const body = requestError instanceof ApiError ? (requestError.body as { error?: string } | null) : null
      showToast('error', body?.error ?? 'No se pudo crear el paciente, inténtalo nuevamente')
    }
  }

  const upgradeLink = (href: string) => (
    <a href={href} className={styles.promptLink} onClick={handleUpgradePromptClick}>
      aquí
    </a>
  )
  const profilePath = `/professionals/health_centers/${healthCenter.id}/edit`

  return (
    <div className="col-md-10 col-md-offset-1 no-padding-xs no-margin-xs">
      {data.patientsTotal <= 1 && !welcomeDismissed && (
        <Alert kind="info" className={styles.firstAlert} data-testid="welcome-alert" onDismiss={dismissWelcome}>
          {data.patientsTotal === 0 ? (
            <>
              <span className={styles.bold}>¡Bienvenido a Historia Clínica! el software de fichas clínicas más simple y seguro</span>
              <br />
              <span className={styles.bold}>No tienes ningún paciente aún. Para ingresar uno haz click en el botón "+ NUEVO PACIENTE"</span>
            </>
          ) : (
            <>
              Ya tienes un paciente creado, para acceder a su ficha clínica haz click en su nombre
              <br />
              Para ingresar otro paciente, haz click en el boton "+ NUEVO PACIENTE"
            </>
          )}
        </Alert>
      )}
      {upgradePrompt === 'account_closed' && (
        <Alert kind="danger" centered data-testid="payment-alert">
          Este centro médico se encuentra temporalmente desactivado por no pago, sólo podrá visitar y descargar la historia clínica de sus
          pacientes.
          <br />
          Puede regularizar sus pagos {upgradeLink(`/health_centers/${healthCenter.id}/payments/new`)}
        </Alert>
      )}
      {(upgradePrompt === 'payment_soon' || upgradePrompt === 'patient_limit') && (
        <Alert kind="warning" centered data-testid="payment-alert">
          {upgradePrompt === 'payment_soon' ? (
            <>
              Próximamente este centro médico se desactivará por no pago, sólo podrá visitar y descargar la historia clínica de sus
              pacientes.
              <br />
              Puede regularizar sus pagos {upgradeLink(profilePath)}. Si ya depositó via transferencia electronica, por favor ignorar este mensaje.
            </>
          ) : (
            <>
              Haz alcanzado el límite de pacientes de tu plan gratuito.
              <br />
              Actualiza tu plan {upgradeLink(profilePath)}
            </>
          )}
        </Alert>
      )}

      <Panel className={styles.container}>
        <PanelBody>
          <PageHeading level={2} className="hidden-sm hidden-md hidden-lg">
            Pacientes
          </PageHeading>
          <CheckboxField
            id="show_archived"
            className={styles.archivedToggle}
            label="Ver Pacientes Archivados"
            checked={showArchived}
            onChange={(checked) => {
              setShowArchived(checked)
              setPage(0)
            }}
          />
          <DataTable
            id="patientTable"
            columns={columns}
            rows={data.patients}
            getRowKey={(row) => row.id}
            tableClassName="patients-table"
            tableStyle={{ width: '100%' }}
            search={searchInput}
            onSearchChange={setSearchInput}
            sort={{ key: String(orderColumn), direction: orderDir }}
            onSort={handleSort}
            page={page}
            pageCount={totalPages}
            onPageChange={setPage}
            total={total}
            from={firstShown}
            to={lastShown}
          >
            <PageHeading level={2} className={`hidden-xs ${styles.floatingHeading}`}>
              Pacientes
            </PageHeading>
          </DataTable>
          <div style={{ float: 'left' }}>
            <ul className="actions list-inline" style={{ float: 'left' }}>
              <li>
                <Dropdown
                  raised
                  buttonClassName="right module-excel-button"
                  label={
                    <>
                      <i className="fa fa-download" /> Descargar
                    </>
                  }
                >
                  <li>
                    <a
                      href={`/health_centers/${healthCenter.id}/patients.xls`}
                      onClick={() => trackEvent('Patients Exported', { format: 'xls' }, { transport: 'sendBeacon' })}
                    >
                      <p>Formato Excel</p>
                    </a>
                  </li>
                </Dropdown>
              </li>
            </ul>
          </div>
          {!closed && (
            <>
              <div style={{ float: 'right' }}>
                <ButtonAnchor
                  href="#modal"
                  raised
                  id="btn_new_patient"
                  onClick={(event) => {
                    event.preventDefault()
                    trackEvent('Patient Modal Opened')
                    setModalOpen(true)
                  }}
                >
                  <i className="fa fa-plus" /> Nuevo Paciente
                </ButtonAnchor>
              </div>
              <NewPatientModal
                open={modalOpen}
                patientsCount={data.patientsTotal}
                onClose={() => setModalOpen(false)}
                onSubmit={handleFindOrCreate}
              />
            </>
          )}
        </PanelBody>
      </Panel>
    </div>
  )
}
